<template>

<div class="container-fluid">
    <page-loading v-if="!data_loaded" />

    <div v-else-if="!goals.length" class="col-12 text-center">
        <div class="mx-auto rounded-circle circle-cropped avatar-xl bg-secondary text-white text-5xl mb-4 mt-5">
            <i class="fad fa-trophy-alt fa-fw"></i>
        </div>
        <div class="font-weight-bold text-2xl text-gray-700">
            Add your first goal
        </div>
        <div class="font-lg text-gray-500 mb-3">
            Track progress for yourself or others
        </div>
        <span class="btn btn-primary btn-lg" data-toggle="modal" data-target="#goalCreateModal" data-test="addGoalButton">Create Goal</span>
    </div>

    <div v-else class="row">
        <div class="col-12">    
            <div class="d-flex align-items-center">
                <h1 class="flex-grow-1" data-test="pageTitle">Active Goals</h1>
                <button 
                    v-if="perms.can_create_goal"
                    type="button" class="btn btn-sm btn-ghost-primary text-gray-500 leading-none"
                    @click="startEditing()" data-toggle="modal" data-target="#goalCreateModal"
                    v-tooltip.bottom="'Add Goal'"
                    data-test="addGoalButton">
                    <i class="far fa-plus-circle text-xl"></i>
                </button>
            </div>  
        </div>

        <div v-for="goal in goals.filter(goal => goal.status === 1)" class="col-12 col-lg-6">
            <goal-item 
                :goal="goal"
                :key="goal.id"
                :can_set_facility_goal="perms.can_set_facility_goal"
                :can_set_any_user_goal="perms.can_set_any_user_goal"
                :can_set_own_user_goal="perms.can_set_own_user_goal"
                @update_goal="updateGoal($event)"
                @delete_goal="deleteGoal($event)"
                @update_goal_status="updateGoalStatus($event)" />
        </div>

        <template v-if="goals.filter(goal => goal.status != 1).length > 0">
            <div @click="toggleInactives()" class="col-12 mb-3 cursor-pointer text-gray-500 text-lg font-weight-bold">
                Show Inactive/Prior Goals <span class="badge badge-secondary text-white">{{ goals.filter(goal => goal.status != 1).length }}</span>
            </div>
            <div v-if="showInactives" v-for="goal in goals.filter(goal => goal.status != 1)" class="col-12 col-lg-6 inactive">
                <goal-item 
                    :goal="goal"
                    :key="goal.id"
                    :can_set_facility_goal="perms.can_set_facility_goal"
                    :can_set_any_user_goal="perms.can_set_any_user_goal"
                    :can_set_own_user_goal="perms.can_set_own_user_goal"
                    @update_goal="updateGoal($event)"
                    @delete_goal="deleteGoal($event)"
                    @update_goal_status="updateGoalStatus($event)" />  
            </div>          
        </template>
    </div>


    <!-- New Goal Modal -->
    <div v-if="perms.can_create_goal" class="modal fade" id="goalCreateModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="text-gray-500 font-weight-bold text-uppercase text-base bg-gray-100 px-3 py-25 rounded-top border-bottom">
                    Create Goal
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body d-flex flex-column">

                    <template v-if="form_step == 1">
                        <div class="text-gray-700 text-2xl text-center my-2">
                            What kind of goal?
                        </div>
                        <div class="flex-grow-1 d-flex text-center justify-content-center align-items-center pb-4 mb-3">
                            <div class="flex-fill" :class="{ 'border-right' : perms.can_set_facility_goal }">
                                <div class="rounded-circle circle-cropped avatar-md text-white text-xl mx-auto mb-3" :class="form.goal_type == 'checkin' ? 'bg-primary' : 'bg-gray-600'">
                                    <i class="fad fa-check-circle" @click="setGoalType('checkin')"></i>
                                </div>
                                <span :class="form.goal_type == 'checkin' ? 'btn btn-secondary text-white' : 'btn btn-light'" @click="setGoalType('checkin')" data-test="newGoalTypeCheckin">Check-ins</span>
                            </div>
                            <template v-if="perms.can_set_facility_goal">
                                <div class="flex-fill border-right">
                                    <div class="rounded-circle circle-cropped avatar-md text-white text-xl mx-auto mb-3" :class="form.goal_type == 'referral' ? 'bg-primary' : 'bg-gray-600'">
                                        <i class="fad fa-user-injured" @click="setGoalType('referral')"></i>
                                    </div>
                                    <span :class="form.goal_type == 'referral' ? 'btn btn-secondary text-white' : 'btn btn-light'" @click="setGoalType('referral')" data-test="newGoalTypeReferral">Referrals</span>
                                </div>
                                <div class="flex-fill">
                                    <div class="rounded-circle circle-cropped avatar-md text-white text-xl mx-auto mb-3" :class="form.goal_type == 'charge' ? 'bg-primary' : 'bg-gray-600'">
                                        <i class="fad fa-wallet" @click="setGoalType('charge')"></i>
                                    </div>
                                    <span :class="form.goal_type == 'charge' ? 'btn btn-secondary text-white' : 'btn btn-light'" @click="setGoalType('charge')" data-test="newGoalTypeCharge">Charges</span>
                                </div>
                            </template>                      
                        </div>
                    </template>

                    <template v-if="form_step == 2">
                        <div class="text-gray-700 text-2xl text-center mt-2 mb-4">
                            Specify the kind of check-in, if desired
                        </div>

                        <div class="flex-grow-1 mx-auto" style="width:300px">
                            <div class="form-group">  
                                <v-select
                                    placeholder="Any Check-in"
                                    :searchable="false"
                                    :options="[
                                        { value: 'checkin', label: 'Any Check-in' },
                                        { value: 'checkin_type', label: 'Check-in Type' },
                                        { value: 'service_line', label: 'Service Line Promoted' },
                                        { value: 'specialty', label: 'Physician Specialty' },
                                    ]"
                                    :reduce="label => label.value"
                                    v-model="form.goal_type"
                                    data-test="newGoalCheckinType"
                                />
                            </div>

                            <div v-show="form.goal_type === 'checkin_type'" class="form-group">
                                <label class="mb-0">Select Type</label>
                                <v-select                    
                                    label="label"                               
                                    :options="options.checkin_types"
                                    :reduce="label => label.id"
                                    v-model="form.checkin_type_id"
                                    data-test="newGoalCheckinTypeSelect" 
                                />
                            </div>

                            <div v-show="form.goal_type === 'service_line'" class="form-group">
                                <label class="mb-0">Select Service Line</label>
                                <v-select                                
                                    label="label"
                                    :options="options.service_lines"
                                    :reduce="label => label.id"
                                    v-model="form.service_lines" />
                            </div>

                            <div v-show="form.goal_type === 'specialty'" class="form-group">
                                <label class="mb-0">Select Specialty</label>
                                <v-select                                
                                    label="label"
                                    :options="options.specialties"
                                    :reduce="label => label.id"
                                    v-model="form.specialties" />
                            </div>
                        </div>

                        <div class="button-wrap border-top text-right">
                            <div class="d-flex justify-content-between pt-3 px-3 w-100">
                                <span class="btn btn-ghost-secondary" @click="setFormStep(1)"><i class="fal fa-long-arrow-left"></i> Change Type</span>
                                <button class="btn btn-primary" @click="perms.can_set_facility_goal || perms.can_set_any_user_goal ? setFormStep(3) :  setFormStep(4)" data-test="newGoalNextStepButton">Next Step <i class="fal fa-long-arrow-right"></i></button>
                            </div>           
                        </div>
                    </template>

                    <template v-if="form_step == 3">
                        <div class="text-gray-700 text-2xl text-center mt-2 mb-4">
                            This goal is for
                        </div>

                        <div class="flex-grow-1">
                            <div class="d-flex text-center justify-content-center align-items-center mb-4">
                                <div class="media-body border-right">
                                    <div class="rounded-circle circle-cropped avatar-md text-white text-xl mx-auto mb-3" :class="form.scope == 'user' && form.user_id == user.id ? 'bg-primary' : 'bg-gray-600'">
                                        <img :src="user.photo_url" class="img-fluid" @click="setGoalScope('self')">
                                    </div>
                                    <span :class="form.scope == 'user' && form.user_id == user.id ? 'btn btn-secondary text-white' : 'btn btn-light'" @click="setGoalScope('self')" style="min-width:60px" data-test="newGoalScopeMe">Me</span>
                                </div>
                                <div v-if="perms.can_set_any_user_goal" class="media-body border-right">
                                    <div class="rounded-circle circle-cropped avatar-md text-white text-xl mx-auto mb-3" :class="form.scope == 'user' && form.user_id != user.id ? 'bg-primary' : 'bg-gray-600'">
                                        <i class="fad fa-user" @click="setGoalScope('user')"></i>
                                    </div>
                                    <span :class="form.scope == 'user' && form.user_id != user.id ? 'btn btn-secondary text-white' : 'btn btn-light'" @click="setGoalScope('user')" data-test="newGoalScopeUser">A User</span>
                                </div>                            
                                <div v-if="perms.can_set_facility_goal" class="media-body">
                                    <div class="rounded-circle circle-cropped avatar-md text-white text-xl mx-auto mb-3" :class="form.scope == 'facility' ? 'bg-primary' : 'bg-gray-600'">
                                        <i class="fad fa-hospital" @click="setGoalScope('facility')"></i>
                                    </div>
                                    <span :class="form.scope == 'facility' ? 'btn btn-secondary text-white' : 'btn btn-light'" @click="setGoalScope('facility')" data-test="newGoalScopeFacility">My Facility</span>
                                </div>                      
                            </div>

                            <div v-if="perms.can_set_any_user_goal && form.scope === 'user' && form.user_id != user.id" class="mx-auto form-group mb-4" style="width:300px">
                                <label class="mb-0">Select User</label>
                                <v-select                    
                                    label="display_name"
                                    :options="options.user_ids"
                                    :reduce="display_name => display_name.id"
                                    v-model="form.user_id"
                                    data-test="newGoalUser">
                                    <template v-slot:option="option">
                                        <div class="d-flex align-items-center py-1">
                                            <div class="rounded-circle circle-cropped avatar-sm mr-2 text-center">
                                                <img :src="option.photo_url" class="img-fluid">
                                            </div>
                                            <div class="flex-grow-1">{{ option.display_name }}</div>
                                        </div>
                                    </template>
                                </v-select>
                            </div>
                        </div>

                        <div class="button-wrap border-top text-right">
                            <div class="d-flex justify-content-between pt-3 px-3 w-100">
                                <span class="btn btn-ghost-secondary" @click="setFormStep(2)"><i class="fal fa-long-arrow-left"></i> Change Check-ins</span>
                                <button :disabled="!form.scope || (form.scope == 'user' && !form.user_id)" class="btn btn-primary" @click="setFormStep(4)" data-test="newGoalNextStepButton">Next Step <i class="fal fa-long-arrow-right"></i></button>
                            </div>
                        </div>
                    </template>

                    <template v-if="form_step == 4">
                        <div class="text-gray-700 text-2xl text-center mt-2 mb-4">
                            Set goal values
                        </div>
                        
                        <div class="flex-grow-1 mx-auto" style="width:350px">
                            <div class="d-flex mb-3">
                                <div class="mr-2 flex-fill">
                                    <label class="mb-0">Start Date</label>
                                    <date-picker 
                                        readonly="true"
                                        class="date-picker"
                                        v-model="form.start_date"
                                        :config="config.datePickerOptions"
                                        data-test="newGoalStart"
                                    />                            
                                </div>
                                <div class="flex-fill" style="position:relative;">
                                    <label class="mb-0">End Date <span class="text-gray-500"> - Optional</span></label>
                                    <i v-if="form.end_date" class="far fa-times-circle cursor-pointer text-gray-400 clear-field" @click="clearField('end_date')"></i>
                                    <date-picker 
                                        readonly="true"
                                        class="date-picker"
                                        v-model="form.end_date"
                                        :config="config.datePickerOptions"
                                         data-test="newGoalEnd"
                                    />  
                                 </div> 
                            </div>


                            <div class="d-flex mb-3">
                                <div class="mr-2 flex-fill w-50" style="position:relative;">
                                    <label class="mb-0">Goal Target</label>
                                    <span v-if="form.goal_type == 'charge'" class="text-gray-600" style="position:absolute; top:30px; left:10px;">$</span>
                                    <input 
                                        v-model="form.target"
                                        :placeholder="form.goal_type == 'charge' ? 'Charges Amount' : form.goal_type == 'referral' ? 'Num. Referrals' : 'Num. Check-ins'" 
                                        class="form-control" :class="{ 'pl-35' : form.goal_type == 'charge'}" type="number" required data-test="newGoalTarget">
                                </div>
                                <div class="flex-fill w-50">
                                    <label class="mb-0">Every</label>
                                    <v-select
                                        :clearable="false"
                                        placeholder="frequency"
                                        :searchable="false"
                                        :options="form.goal_type == 'referral' || form.goal_type == 'charge' ? [
                                            { value: 'week', label: 'Week' },
                                            { value: 'month', label: 'Month' },
                                        ] : [
                                            { value: 'day', label: 'Day' },
                                            { value: 'week', label: 'Week' },
                                            { value: 'month', label: 'Month' },
                                        ]"
                                        :reduce="label => label.value"
                                        v-model="form.frequency"
                                        data-test="newGoalFreq"
                                    />                     
                                </div>
                            </div>
                        </div>

                        <div class="button-wrap border-top text-right">
                            <div class="d-flex justify-content-between pt-3 px-3 w-100">
                                <span v-if="form.goal_type == 'referral' || form.goal_type == 'charge'" class="btn btn-ghost-secondary" @click="setFormStep(1)"><i class="fal fa-long-arrow-left"></i> Change Type</span>
                                <span v-else class="btn btn-ghost-secondary" @click="setFormStep(3)"><i class="fal fa-long-arrow-left"></i> Change Assigment</span>
                                <button :disabled="!form.start_date || !form.target || !form.frequency" class="btn btn-primary" @click="setFormStep(5)">Next Step <i class="fal fa-long-arrow-right" data-test="newGoalNextStepButton"></i></button>
                            </div>
                        </div>
                    </template>

                    <template v-if="form_step == 5">      

                        <template v-if="!form.errors.any()">
                            <div class="text-gray-700 text-2xl text-center mt-2 mb-4">
                                Name and save your goal
                            </div>

                            <div class="flex-grow-1 mx-auto" style="width:350px">
                                <div class="form-group">
                                    <label class="mb-0">Title</label>
                                    <input v-model="form.title" class="form-control" required data-test="newGoalTitle">
                                </div>

                                <div class="font-weight-bold text-blue-600">Goal Details:</div>
                                <ul class="list-unstyled">
                                    <li class="leading-loose">
                                        <i class="fal fa-long-arrow-right text-primary"></i>
                                        <span v-if="form.goal_type == 'referral'">{{ form.target }} referrals</span> 
                                        <span v-else-if="form.goal_type == 'charge'">${{ form.target }} in charges</span> 
                                        <span v-else>{{ form.target }} check-ins 
                                            <span v-if="form.goal_type == 'checkin_type'">of type {{ options.checkin_types.find(element => element.id == form.checkin_type_id).label }}</span>
                                            <span v-if="form.goal_type == 'specialty'">with {{ options.specialties.find(element => element.id == form.specialties).label }}</span>
                                            <span v-if="form.goal_type == 'service_line'">promoting  {{ options.service_lines.find(element => element.id == form.service_lines).label }}</span>
                                        </span>                                 
                                        every {{ form.frequency }}
                                    </li>
                                    <li class="leading-loose">
                                        <i class="fal fa-long-arrow-right text-primary"></i>
                                        from {{ form.start_date }} until <span v-if="form.end_date">{{ form.end_date }}</span><em v-else>indefinitely</em>
                                    </li>
                                    <li class="leading-loose">
                                        <div v-if="form.scope == 'user'" class="d-flex align-items-center">
                                            <i class="fal fa-long-arrow-right text-primary"></i>
                                            <span>&nbsp;for </span>
                                            <div class="rounded-circle circle-cropped avatar-xs mx-1 text-center">
                                                <img :src="options.user_ids.find(element => element.id == form.user_id).photo_url" class="img-fluid">
                                            </div>
                                            <div class="flex-grow-1">{{ options.user_ids.find(element => element.id == form.user_id).display_name }}</div>
                                        </div>
                                        <span v-else>
                                            <i class="fal fa-long-arrow-right text-primary"></i>
                                            for the facility ({{ user.client.name }})
                                        </span>
                                    </li>
                                </ul>      
                            </div>
                        </template>

                        <template v-else>
                            <alert-errors :form="form">There were some problems with your input.</alert-errors>

                            <div class="flex-grow-1 mx-auto" style="width:350px">

                                <div v-if="form.errors.errors.title" class="form-group">
                                    <label class="mb-0">Title</label>
                                    <input v-model="form.title" class="form-control" required>
                                </div>

                                <div v-if="form.errors.errors.goal_type" class="form-group">
                                    <label class="mb-0">Goal Type</label>
                                    <v-select                                    
                                        placeholder="Select one"
                                        :searchable="false"
                                        :options="[
                                            { value: 'checkin', label: 'Check-ins' },
                                            { value: 'referral', label: 'Referrals' },
                                            { value: 'charge', label: 'Charges' },
                                        ]"
                                        :reduce="label => label.value"
                                        v-model="form.goal_type"
                                    />
                                </div>

                                <div v-if="form.errors.errors.scope" class="form-group">
                                    <label class="mb-0">Goal Scope</label>
                                    <v-select                                    
                                        placeholder="Select one"
                                        :searchable="false"
                                        :options="[
                                            { value: 'user', label: 'User' },
                                            { value: 'facility', label: 'Facility' },
                                        ]"
                                        :reduce="label => label.value"
                                        v-model="form.scope"
                                    />
                                </div>

                                <div v-if="form.errors.errors.start_date" class="form-group">
                                    <label class="mb-0">Start Date</label>
                                    <date-picker 
                                        readonly="true"
                                        class="date-picker"
                                        v-model="form.start_date"
                                        :config="config.datePickerOptions" />                            
                                </div>

                                <div v-if="form.errors.errors.end_date" class="form-group">
                                    <label class="mb-0">End Date <span class="text-gray-500"> - Optional</span></label>
                                    <date-picker 
                                        readonly="true"
                                        class="date-picker"
                                        v-model="form.end_date"
                                        :config="config.datePickerOptions" />  
                                 </div>

                                <div v-if="form.errors.errors.target" class="form-group">
                                    <label class="mb-0">Goal Target</label>
                                    <input 
                                        v-model="form.target"
                                        :placeholder="form.goal_type == 'charge' ? 'Charges Amount' : form.goal_type == 'referral' ? 'Num. Referrals' : 'Num. Check-ins'" 
                                        class="form-control" type="number" required>
                                </div>

                                <div v-if="form.errors.errors.frequency" class="form-group">
                                    <label class="mb-0">Frequency</label>
                                    <v-select
                                        :clearable="false"
                                        placeholder="Select on"
                                        :searchable="false"
                                        :options="form.goal_type == 'referral' || form.goal_type == 'charge' ? [
                                            { value: 'week', label: 'Week' },
                                            { value: 'month', label: 'Month' },
                                        ] : [
                                            { value: 'day', label: 'Day' },
                                            { value: 'week', label: 'Week' },
                                            { value: 'month', label: 'Month' },
                                        ]"
                                        :reduce="label => label.value"
                                        v-model="form.frequency"
                                    />                     
                                </div>
                            </div>
                        </template>

                        <div class="button-wrap border-top text-right">
                            <div class="d-flex justify-content-between pt-3 px-3 w-100">
                                <span class="btn btn-ghost-secondary" @click="setFormStep(4)"><i class="fal fa-long-arrow-left"></i> Change Values</span>
                                <button :disabled="!form.title" class="btn btn-primary" @click="saveGoal()" data-test="saveNewGoalButton">Save Goal</button>
                            </div>
                        </div>
                    </template>
                </div>

            </div>
        </div>
    </div>


</div>
</template>


<script>

    import { mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';

    import GoalItem from '../../components/GoalItem';
    import { Form, AlertError, AlertErrors, AlertSuccess } from 'vform';

    export default {
        
        mixins: [BreadcrumbsManager],
        
        components: {
            GoalItem,
            AlertError,
            AlertErrors,
            Form,
            AlertSuccess,
        },

        data() {
            return {
                data_loaded: false,
                state: 'default',

                datePickerOptions: {
                    format: 'MMM D, YYYY',
                },

                form: new Form(),    

                form_step: 1,

                goals: [],
                showInactives: false,
            }
        },

        created() {
            this.$store.dispatch('fetchUserIds');
            this.$store.dispatch('fetchSpecialties');           
        },

        activated() {
            this.fetchGoals();
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Manage Goals', to: { name: 'goal.index' } },
            ]);  
        },

        mounted() {
        },

        methods: {
            clearField(field) {
                this.form[field] = null;
            },

            deleteGoal($event) {
                Swal.fire({
                      title: 'Are you sure?',
                      text: 'You will not be able to recover this goal.',
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonText: 'Delete goal',
                      cancelButtonText: 'Keep goal',
                      animation: false,
                    }).then((result) => {
                        if (result.value) {
                            axios.delete(`/api/goals/${$event.id}`, $event).then(() => {
                                this.goals.splice(this.goalIndex($event.id), 1);
                            })
                        }
                    }
                );
            },

            fetchGoals() {
                axios.get('/api/goals/client/' + this.user.client_id).then(({data}) => {
                    this.goals = data;
                    this.data_loaded = true;
                })
            },

            goalIndex(goalId) {
                return this.goals.findIndex((element) => {
                    return element.id === goalId;
                });
            },             

            resetForm() {
                this.form = new Form({
                    title: null,
                    scope: null,
                    user_id: null,
                    start_date: null,
                    end_date: null,
                    target: null,
                    frequency: null,
                    goal_type: null,
                    checkin_type_id: null,
                    service_lines: null,
                    specialties: null,                 
                });
                this.form_step = 1;
                this.setScope();
            },

            saveGoal() {
                this.form.post('/api/goals').then(({ data }) => { 
                    this.fetchGoals();
                    this.resetForm();

		        	//forgive me, for I have sinned
		        	jQuery('#goalCreateModal').modal('hide');

					Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        icon: 'success',
                        title: 'Goal saved',
                    });	                          
                })
            },

            setFormStep(value) {
                this.form_step = value;
            },

            setGoalScope(value) {
                if (value == 'self') {
                    this.form.scope = 'user';
                    this.form.user_id = this.user.id;
                } else {
                    this.form.scope = value;
                    this.form.user_id = null;
                }

                if (value != 'user') {
                    this.form_step = 4;
                }
            },

            setGoalType(value) {
                this.form.goal_type = value;
                if (value == 'checkin') {
                    this.form_step = 2;
                } else { 
                    //referral or charge goal; also can set scope here and clear stuff if it was set
                    this.form.scope = 'facility';
                    this.form.checkin_type_id = null;
                    this.form.specialties = null;
                    this.form.service_lines = null;
                    this.form.user_id = null;
                    this.form_step = 4;
                }  
            },

            setScope() {
                if (!this.perms.can_set_facility_goal) {
                    this.form.scope = 'user';
                    this.form.user_id = this.user.id;
                }
            }, 

            startEditing() {
                this.state = 'editing';
                this.resetForm();
            }, 

            stopEditing() {
                this.state = 'default';
            },   

            toggleInactives() {
                this.showInactives = !this.showInactives;
            },

            updateGoal($event) {
                //delete and then re-add
                this.goals.splice(this.goalIndex($event.id), 1);
                this.goals.push($event);
            },

            updateGoalStatus($event) {
                this.goals[this.goalIndex($event.id)].status = $event.status;
            },

          
        },

        computed: {
            ...mapState(['config', 'options', 'perms', 'user'])
        }

    }
</script>

<style>
    #goalCreateModal .modal-body { 
        min-height:360px;
    }

    #goalCreateModal .faded {
        opacity: .5;
    }

    #goalCreateModal .button-wrap {
        margin:0 -1rem; 
    }

    #goalCreateModal .vs__dropdown-toggle {
        padding: 4px 0px 6px !important;
    }

    #goalCreateModal .clear-field {
        position:absolute;
        top:33px;
        right:10px; 
    }
</style>